import * as React from 'react';
import { CSSTransition } from 'react-transition-group';

import { HiddenRenderer } from '../HiddenRenderer';
import { getPropsAnimatorClassNames } from './PropsAnimator.classNames';

export interface InState<InjectedProps, TInState> {
  /** Additional props made availble when animating in some new props */
  animationProps?: InjectedProps;
  /** Provided to shouldAnimateOut along with incoming props to determine if we should animate old props out, and new props in  */
  state?: TInState;
}

export interface OutState<TProps> {
  /**
   * When an animate out is detected, we render the new props hidden, and
   * combine those new props with offsetExitedProps to "fake" the previous props and apply the exit animation
   */
  offsetExitedProps: Partial<TProps>;
  hiddenPropOverrides?: Partial<TProps>;
}

export interface PropsAnimatorState<OriginalProps, InjectedProps, TInState> {
  animatingIn?: InState<InjectedProps, TInState>;
  animatingOut?: OutState<OriginalProps>;
}

export interface PropsAnimatorOptions<OriginalProps, InjectedProps, TInState, THiddenRenderData> {
  /**
   * Class names and durations to use with the CSSTransition component used to animate between props
   */
  transition: { className: CSSTransition.CSSTransitionClassNames | string; inDuration: number; outDuration: number };
  /**
   * Should we animate this component at all?
   */
  shouldAnimate: (nextProps: OriginalProps) => boolean;
  /**
   * Get the inState associated with the first render of the component
   */
  getInitialInState: (nextProps: OriginalProps) => InState<InjectedProps, TInState>;
  /**
   * The old props have been animated out, get the InState needed to render the new props in
   */
  getInState: (
    nextProps: OriginalProps,
    previousOutState: OutState<OriginalProps>,
    visibleWrapper: HTMLElement,
    valueFromHiddenRender: THiddenRenderData
  ) => InState<InjectedProps, TInState>;
  /**
   * New props incoming. Should we animate out the old props?
   */
  shouldAnimateOut: (nextProps: OriginalProps, inState: InState<InjectedProps, TInState>) => boolean;
  /**
   * Get a cache key to cache the result of getDataFromHiddenRender
   */
  getCacheKeyForHiddenRender?: (props: OriginalProps) => string;
  /**
   * @param hiddenWrapper The component rendered hidden with the new props.
   * @returns Any data needed by getInState to setup the Enter animation
   */
  getDataFromHiddenRender: (hiddenWrapper: HTMLElement) => THiddenRenderData;
  /**
   * Get the state needed to Exit the previous props
   */
  getOutState: (nextProps: OriginalProps, previousInState: InState<InjectedProps, TInState>) => OutState<OriginalProps>;
}

/**
 * Adds the ability for a component to animate its prop changes.
 * This higher-order component should be used when you first need to render the incoming component props in order to properly animate to the next
 * set of props. Conceptually, there are two states to the component "in" (the new props are being animated in) and "out" (the old props are being animated out)
 * Here's the flow of animation from "in" to "out", back to "in" as props change for a component:
 *
 * 1. Render()
 *      - Visible: Render the current props combined with animationProps from InState. CSSTransition: Enter is applied
 * 2. getDerivedStateFromProps()
 *      - A prop change has happened. Call shouldAnimateOut()
 * 3. If shouldAnimateOut returns false:
 *      - Render()
 *        - Visible: The new props.
 *    If shouldAnimateOut returns true:
 *      - Call getOutState(nextProps, previousInState)
 *      - Render()
 *        - Visible: The new props, but combined with outState.offsetExitedProps appear like the previous props. CSSTransition: Exit
 *        - Hidden: The new props
 * 4. Call getDataFromHiddenRender()
 * 5. Once the visible props have exited, call getInState(), back to #1 above
 *
 * For the initial render, the result of getInitialInState is used in step #1.
 *
 * As an example usage, consider the case of dropping a label from a button, animating from initial prop hideLabel: false to hideLabel: true.
 * To aid animation, the Button takes additional props initialWidth: number and finalWidth: number used to by CSSTransition.enter/enterActive to animate between widths when specified.
 *
 * 0. getInitialInState(): Returns { state: { hideLabel: false } };
 * 1. Render
 *      - Visible: current props (no animationProps provided in step 0), initialWidth and finalWidth are undefined
 * 2. getDerviedStateFromProps()
 *      - nextProps have hideLabel: true.
 * 3. shouldAnimateOut(nextProps, inState): nextProps.hideLabel !== inState.hideLabel. Return true
 *      - getOutState(nextProps, previousInState) returns { offsetExitedProps: { hideLabel: inState.hideLabel } }
 *      - Render()
 *        - Visible: The button with the label still shown, CSSTransition: Exit fades out the label
 *        - Hidden: The button with the label dropped
 * 4. getDataFromHiddenRender() returns the width of the button without the label
 * 5. getInState() measures the width of the button rendered with the label visible. returns { animationProps: { initialWidth: widthWithLabel, finalWidth: widthWithoutLabel }, state: { hideLabel: true } }
 * (back to step 1)
 * 1. Render
 *  - Visible: current props, which now have initialWidth and finalWidth specified, CSSTransition: Enter is played animating between the widths
 */
export const animatePropsChanges = <OriginalProps extends {}, InjectedProps, TInState, THiddenRenderData>(
  options: PropsAnimatorOptions<OriginalProps, InjectedProps, TInState, THiddenRenderData>
) => (Component: React.ComponentType<OriginalProps & Partial<InjectedProps>>) => {
  class PropsAnimator extends React.PureComponent<
    OriginalProps & { forwardedRef: React.Ref<typeof Component> },
    PropsAnimatorState<OriginalProps, InjectedProps, TInState>
  > {
    private visibleRef: React.RefObject<HTMLDivElement> = React.createRef();
    private hiddenRef: React.RefObject<HTMLDivElement> = React.createRef();
    private dataFromHiddenRender: THiddenRenderData | undefined;

    constructor(props: OriginalProps & { forwardedRef: React.Ref<typeof Component> }) {
      super(props);
      this.state = {};
    }

    static getDerivedStateFromProps(
      nextProps: OriginalProps,
      previousState: PropsAnimatorState<OriginalProps, InjectedProps, TInState>
    ): PropsAnimatorState<OriginalProps, InjectedProps, TInState> | null {
      if ((!previousState.animatingIn && !previousState.animatingOut) || !options.shouldAnimate(nextProps)) {
        // Step 0. Setup initial state for component
        return {
          animatingIn: options.getInitialInState(nextProps),
          animatingOut: undefined,
        };
      }

      // Step 2: Check if we should animate out
      if (previousState.animatingIn && options.shouldAnimateOut(nextProps, previousState.animatingIn)) {
        // Step 3: Time to animate out the old props
        return {
          animatingIn: undefined,
          animatingOut: options.getOutState(nextProps, previousState.animatingIn),
        };
      }

      // Step 1: No special props treatment needed
      return null;
    }

    public render() {
      return this.renderInternal(!!options.shouldAnimate(this.props));
    }

    private renderInternal(animated: boolean): JSX.Element {
      let hiddenProps: OriginalProps | undefined;
      let transitionDuration: number;
      let inValue: boolean;
      let offsetExitedProps: Partial<OriginalProps> | undefined;
      let enter: boolean;
      let exit: boolean;

      if (animated) {
        hiddenProps = this.state.animatingOut
          ? { ...this.props, ...this.state.animatingOut.hiddenPropOverrides }
          : undefined;
        inValue = !this.state.animatingOut;
        transitionDuration = inValue ? options.transition.inDuration : options.transition.outDuration;
        offsetExitedProps = this.state.animatingOut ? this.state.animatingOut.offsetExitedProps : undefined;
        enter = !!this.state.animatingIn;
        exit = !!this.state.animatingOut;
      } else {
        transitionDuration = 0;
        inValue = true;
        enter = false;
        exit = false;
      }

      const animationProps = this.state.animatingIn && this.state.animatingIn.animationProps;
      const classNames = getPropsAnimatorClassNames();

      return (
        <React.Fragment>
          <CSSTransition
            in={inValue}
            enter={enter}
            exit={exit}
            onExited={this.onLayoutExited}
            classNames={options.transition.className}
            timeout={transitionDuration}
          >
            <div ref={this.visibleRef} className={classNames.wrapperElement}>
              <Component
                ref={this.props.forwardedRef}
                {...this.props}
                // Step #3 - Visible
                {...(offsetExitedProps as any)}
                // Step #1 - Visible
                {...(animationProps as any)}
              />
            </div>
          </CSSTransition>
          {/* Step #3 - Hidden */}
          <HiddenRenderer
            onRendered={hiddenProps && this.onHiddenPropsRendered}
            cacheKey={
              hiddenProps && options.getCacheKeyForHiddenRender && options.getCacheKeyForHiddenRender(hiddenProps)
            }
          >
            {hiddenProps && (
              <div ref={this.hiddenRef} className={classNames.wrapperElement}>
                <Component {...hiddenProps} />
              </div>
            )}
          </HiddenRenderer>
        </React.Fragment>
      );
    }

    private onHiddenPropsRendered = (cachedValue?: THiddenRenderData): THiddenRenderData => {
      if (cachedValue != null) {
        this.dataFromHiddenRender = cachedValue;
        return cachedValue;
      }

      const valueToCache = options.getDataFromHiddenRender(this.hiddenRef.current!);
      this.dataFromHiddenRender = valueToCache;
      return valueToCache;
    };

    private onLayoutExited = () => {
      this.setState({
        animatingOut: undefined,
        // Step 5: Props old props have exited, tigger another render with the new inState
        animatingIn: options.getInState(
          this.props,
          this.state.animatingOut!,
          this.visibleRef.current!,
          this.dataFromHiddenRender!
        ),
      });
    };
  }

  return React.memo(
    React.forwardRef<typeof Component, OriginalProps & { animate?: boolean }>((props, ref) => {
      return props.animate ? (
        <PropsAnimator {...props} forwardedRef={ref} />
      ) : (
        <Component {...props} forwardedRef={ref} />
      );
    })
  );
};
